"use client";

import { useEffect, useState } from "react";
import { Users, Activity } from "lucide-react";

import SabineHero from "@/components/home/SabineHero";
import BroadcastSlim from "@/components/home/BroadcastSlim";
import RepDashboard, {
  FeaturedResources,
} from "@/components/home/RepDashboard";
import AdminDashboard from "@/components/home/AdminDashboard";
import { WorkspaceLogo } from "@/components/nav/WorkspaceSwitcher";
import { cn } from "@/lib/utils";

const SWITCH_OPTIONS = [
  { key: "admin", label: "Team", Icon: Users },
  { key: "rep", label: "My practice", Icon: Activity },
];

/** Sleek segmented role switch — sizes to its content. */
function RoleSwitch({ view, setView }) {
  return (
    <div
      role="tablist"
      aria-label="Dashboard view"
      className="inline-flex gap-0.5 rounded-xl border border-gray-200 bg-gray-50 p-0.5"
    >
      {SWITCH_OPTIONS.map(({ key, label, Icon }) => {
        const active = view === key;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => setView(key)}
            className={`inline-flex items-center gap-1.5 rounded-[9px] px-3 py-1.5 text-[12.5px] transition-colors ${
              active
                ? "bg-white font-semibold text-gray-900 shadow-sm ring-1 ring-gray-200"
                : "font-medium text-gray-500 hover:text-gray-900"
            }`}
          >
            <Icon
              className={`h-3.5 w-3.5 transition-colors ${
                active ? "text-brand-600" : "text-gray-400"
              }`}
            />
            {label}
          </button>
        );
      })}
    </div>
  );
}

export default function HomeShell({
  userName,
  greeting = "Welcome back",
  userId,
  workspaceId,
  studio,
  isManager = false,
  canBroadcast = false,
  defaultView = "rep",
  repData,
  adminData = null,
  repPrompts = [],
  adminPrompts = [],
  repPlaceholder,
  adminPlaceholder,
}) {
  const hasAdmin = isManager && Boolean(adminData);
  const [view, setView] = useState(hasAdmin ? defaultView : "rep");
  const showingAdmin = hasAdmin && view === "admin";

  // Localized date is computed after mount so the server/first-client render
  // match (no hydration mismatch); the eyebrow simply reveals once it's set.
  const [today, setToday] = useState("");
  useEffect(() => {
    setToday(
      new Date().toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
      })
    );
  }, []);

  return (
    <div className="mx-auto w-full max-w-[880px] px-4 pb-24 pt-8 sm:px-6 sm:pt-10">
      {/* The studio's logo opposite the role switch. Below md the mobile
          header already shows it, so only the switch (if any) stays. */}
      <div
        className={cn(
          "animate-rise mb-6 items-center justify-between gap-5",
          hasAdmin ? "flex" : "hidden md:flex"
        )}
      >
        <div className="hidden md:block">
          <WorkspaceLogo workspace={studio} />
        </div>
        {hasAdmin && <RoleSwitch view={view} setView={setView} />}
      </div>

      <div className="animate-rise mb-6">
        <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em]">
          <span className="text-brand-600">Today</span>
          {today && (
            <>
              <span aria-hidden className="text-gray-300">
                ·
              </span>
              <span className="text-gray-400">{today}</span>
            </>
          )}
        </div>
        <h1 className="text-[28px] font-semibold leading-tight tracking-tight text-gray-900 sm:text-[32px]">
          {greeting}, {userName || "there"}.
        </h1>
      </div>

      <div className="animate-rise" style={{ "--rise-delay": "60ms" }}>
        <BroadcastSlim canBroadcast={canBroadcast} />
      </div>

      <div className="animate-rise" style={{ "--rise-delay": "120ms" }}>
        <SabineHero
          userId={userId}
          workspaceId={workspaceId}
          prompts={showingAdmin ? adminPrompts : repPrompts}
          placeholder={showingAdmin ? adminPlaceholder : repPlaceholder}
        />
      </div>

      {showingAdmin ? (
        <AdminDashboard initialData={adminData} initialPeriod={30} />
      ) : (
        <RepDashboard data={repData} />
      )}

      <FeaturedResources featured={repData?.featured} />
    </div>
  );
}
